import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RenumberInstallmentRequest {
  newInstallmentNumber: number;
  comment: string;
}

export interface ChangeDueDateRequest {
  newDueDate: string; // ISO 8601
  comment: string;
}

export interface DeleteInstallmentRequest {
  comment: string;
  forceIfHasReceipts?: boolean;
}

export type AdminPaymentStatus = 'PENDING' | 'PAID' | 'OVERDUE' | 'PARTIALLY_PAID' | 'LINK_GENERATED' | 'AUTO';

export interface ChangeStatusRequest {
  newStatus: AdminPaymentStatus;
  comment: string;
}

export interface AttachReceiptRequest {
  receiptId: number;
  comment: string;
  forceFromOtherEnrollment?: boolean;
}

export interface PunishRemainingRequest {
  comment: string;
}

export interface MoveToVersionRequest {
  targetVersionId: number;
  comment: string;
  /**
   * 🆕 Confirma explícitamente que se acepta modificar Enrollment.TotalAmount
   * si el movimiento lo altera. Cuando false (default), el backend responde con
   * requiresConfirmation=true sin aplicar cambios. Solo aplica a individuales.
   */
  confirmTotalChange?: boolean;
  /**
   * 🆕 Preservar TotalAmount: si true, el backend NO valida ni modifica
   * Enrollment.TotalAmount. La suma de cuotas activas puede quedar diferente
   * — se surfacea por el flag "Necesita atención" del listado.
   */
  preserveTotalAmount?: boolean;
}

/**
 * 🆕 Batch move: mueve N cuotas a una versión destino en una sola transacción.
 * `installmentIds` viene en el orden de selección del operador.
 */
export interface MoveBatchToVersionRequest {
  installmentIds: number[];
  targetVersionId: number;
  comment: string;
  /** Si true, las movidas conservan el orden de selección. Default false (por DueDate). */
  useSelectionOrderForMoved?: boolean;
  confirmTotalChange?: boolean;
  /** Idéntico a MoveToVersionRequest.preserveTotalAmount. */
  preserveTotalAmount?: boolean;
}

/** 🆕 Consolidar versión activa: mueve todas las cuotas IsActive=true a la versión activa. */
export interface ConsolidateActiveVersionRequest {
  comment: string;
}

/**
 * 🆕 Cambiar manualmente cuál PaymentPlanVersion queda como ACTIVA.
 * Sólo flipea el flag IsActive a nivel versión. No mueve cuotas ni renumera.
 */
export interface SetActiveVersionRequest {
  targetVersionId: number;
  comment: string;
}

/**
 * 🆕 Promover un grupo virtual (cuotas con PaymentPlanVersionId=null agrupadas
 * por PaymentPlan.Version) a una PaymentPlanVersion real y dejarla ACTIVA.
 */
export interface PromoteGroupToVersionRequest {
  /** Valor de PaymentPlan.Version del grupo a promover. */
  legacyVersion: number;
  comment: string;
  /**
   * Si true (default), tras crear la nueva versión también enlaza a ella las
   * cuotas huérfanas (PaymentPlanVersionId=null) de OTROS grupos legacy de la
   * misma inscripción. NO toca cuotas ya enlazadas a otras versiones reales.
   */
  sweepOtherOrphans?: boolean;
}

/** 🆕 Activar una cuota archivada dentro de su versión actual. */
export interface ActivateInstallmentRequest {
  comment: string;
}

/** 🆕 Activar N cuotas archivadas en una sola transacción. */
export interface ActivateInstallmentsBatchRequest {
  installmentIds: number[];
  comment: string;
}

export interface AdminInstallmentActionResponse {
  success: boolean;
  message: string;
  /** 🆕 La acción necesita confirmación explícita; backend no aplicó cambios. */
  requiresConfirmation?: boolean;
  /** 🆕 Tipo de confirmación, ej: "TOTAL_AMOUNT_CHANGE". */
  confirmationKey?: string;
  /** 🆕 Valor actual del campo afectado (informativo). */
  currentValue?: number;
  /** 🆕 Valor propuesto del campo tras aplicar la acción. */
  proposedValue?: number;
}

/**
 * 🔐 Acciones admin sobre cuotas individuales. Protegidas en backend por email
 * (solo jdanigo@cloudsoft.site). En frontend se ocultan los botones si el
 * usuario actual no coincide para no generar 403 ruidosos.
 */
@Injectable({ providedIn: 'root' })
export class PaymentPlanAdminService {
  private readonly baseUrl = `${environment.apiUrl}/admin/payment-plans`;

  constructor(private http: HttpClient) {}

  renumber(installmentId: number, request: RenumberInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/renumber`, request);
  }

  changeDueDate(installmentId: number, request: ChangeDueDateRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/change-due-date`, request);
  }

  delete(installmentId: number, request: DeleteInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/delete`, request);
  }

  changeStatus(installmentId: number, request: ChangeStatusRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/change-status`, request);
  }

  attachReceipt(installmentId: number, request: AttachReceiptRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/attach-receipt`, request);
  }

  /** Recalcula PaymentStatus en base a recibos. Sin payload (idempotente). */
  recalculateStatus(installmentId: number)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/recalculate-status`, {});
  }

  /** 🆕 Recalcula PaymentStatus de TODAS las cuotas activas del enrollment. */
  recalculateAll(enrollmentId: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/enrollments/${enrollmentId}/recalculate-all`, {});
  }

  /** 🆕 Cuotas eliminadas (papelera). Permiso: ENROLLMENTS_PAYMENT_PLAN_TRASH. */
  getTrash(enrollmentId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/enrollments/${enrollmentId}/trash`);
  }

  /** 🆕 Restaura una cuota eliminada a la versión activa. */
  restore(installmentId: number, comment: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/restore`, { comment });
  }

  /**
   * Castiga el saldo restante de una cuota parcialmente pagada: cierra la
   * original al monto pagado y crea una nueva cuota castigada por el faltante.
   */
  punishRemaining(installmentId: number, request: PunishRemainingRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/punish-remaining`, request);
  }

  /**
   * Mueve una cuota a otra versión del plan. Si la versión destino es la activa,
   * también reactiva la cuota. Renumera la versión destino por DueDate ascendente.
   */
  moveToVersion(installmentId: number, request: MoveToVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/move-to-version`, request);
  }

  /**
   * 🆕 Batch move: mueve N cuotas a una versión destino en una sola transacción.
   * El orden del array `installmentIds` representa la selección del operador.
   */
  moveBatchToVersion(request: MoveBatchToVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/move-batch-to-version`, request);
  }

  /**
   * 🆕 Consolidar versión activa: para `enrollmentId`, mueve TODAS las cuotas
   * IsActive=true a la versión activa, renumera por fecha. No toca TotalAmount.
   */
  consolidateActiveVersion(enrollmentId: number, request: ConsolidateActiveVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/enrollments/${enrollmentId}/consolidate-active`,
      request
    );
  }

  /**
   * 🆕 Cambiar manualmente cuál PaymentPlanVersion queda como ACTIVA. Setea
   * IsActive=true en la target y IsActive=false en todas las demás. NO mueve
   * cuotas, NO renumera. Sólo cambio de flag a nivel versión.
   */
  setActiveVersion(enrollmentId: number, request: SetActiveVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/enrollments/${enrollmentId}/set-active-version`,
      request
    );
  }

  /**
   * 🆕 Promueve un grupo virtual (cuotas huérfanas con PaymentPlanVersionId=null)
   * a una PaymentPlanVersion real y la deja como ACTIVA. La nueva versión arranca
   * con VersionNumber = legacyVersion (respeta la columna Version legacy).
   * Desactiva la PaymentPlanVersion activa anterior. Renumera 1..N por DueDate.
   */
  promoteGroupToVersion(enrollmentId: number, request: PromoteGroupToVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/enrollments/${enrollmentId}/promote-group-to-version`,
      request
    );
  }

  /**
   * 🆕 Reordena las cuotas activas de la inscripción según el array de ids
   * recibido. Drag-and-drop desde UI.
   */
  reorder(enrollmentId: number, orderedIds: number[])
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/enrollments/${enrollmentId}/reorder`,
      { orderedIds }
    );
  }

  /**
   * 🆕 Activa manualmente una cuota archivada (IsActive=false → true) dentro de
   * su versión actual. Renumera por DueDate la versión afectada.
   * NO modifica Enrollment.TotalAmount.
   */
  activate(installmentId: number, request: ActivateInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/activate`, request);
  }

  /**
   * 🆕 Activa N cuotas archivadas en una sola transacción. Renumera UNA vez por
   * versión afectada al final. NO modifica Enrollment.TotalAmount.
   */
  activateBatch(request: ActivateInstallmentsBatchRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/activate-batch`, request);
  }
}
